import React, { useEffect } from 'react';

import { di } from 'react-magnetic-di';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { useResolveAgentAri } from '@atlassian/conversation-assistant-agent/controllers/agent-ari';

import { useGlobalConfig } from '../../../common/utils/global-config/useGlobalConfig';
import { useChatStoreActions } from '../../../controllers/chat-store/useChatStoreActions';
import { useCurrentConversationId } from '../../../controllers/chat-store/useCurrentConversationId';
import { useCurrentChatConversation } from '../../../controllers/conversation/useCurrentChatConversation';

import type { AgentVersionResolverPermissionsQuery } from './__generated__/AgentVersionResolverPermissionsQuery.graphql';
import type { AgentVersionResolverVersionCapabilityQuery } from './__generated__/AgentVersionResolverVersionCapabilityQuery.graphql';

export type ResolvedAgentVersionAccess = {
	agentAri: string | null;
	canManage: boolean;
	hasVersionCapability: boolean;
};

type AgentVersionResolverProps = {
	agentId: string;
	agentAri?: string | null;
	children: (resolvedAccess: ResolvedAgentVersionAccess) => React.ReactNode;
	fallback: React.ReactNode;
};

/**
 * Establishes the conversation's agentVersion before rendering version-dependent content.
 *
 * Flow:
 * 1. Resolves the agent ARI, tenant version capability, and user's canManage permission
 * 2. Preserves the conversation's existing agentVersion when it has one
 * 3. Otherwise initializes the conversation to DRAFT for managers, or LIVE for everyone else
 * 4. Renders the fallback until the conversation store exposes the initialized agentVersion
 *
 * This keeps the version picker and conversation starters on the same agentVersion instead of
 * letting them independently interpret an absent version.
 */
export const AgentVersionResolver = ({
	agentId,
	agentAri: existingAgentAri,
	children,
	fallback,
}: AgentVersionResolverProps): React.JSX.Element => {
	di(
		useResolveAgentAri,
		useGlobalConfig,
		useChatStoreActions,
		useCurrentConversationId,
		useCurrentChatConversation,
	);
	const { siteId } = useGlobalConfig();
	const currentConversationId = useCurrentConversationId();
	const { agentVersion } = useCurrentChatConversation();
	const { setConversationData } = useChatStoreActions();
	const { agentAri } = useResolveAgentAri({
		agentId,
		existingAgentAri,
		cloudId: siteId,
	});

	const versionCapabilityData = useLazyLoadQuery<AgentVersionResolverVersionCapabilityQuery>(
		graphql`
			query AgentVersionResolverVersionCapabilityQuery($cloudId: String!) {
				agentStudio_hasVersionCapability(cloudId: $cloudId) @optIn(to: "AgentStudio") {
					__typename
					... on AgentStudioHasVersionCapability {
						hasVersionCapability
					}
				}
			}
		`,
		{
			cloudId: siteId,
		},
	);
	const hasVersionCapability =
		versionCapabilityData?.agentStudio_hasVersionCapability?.__typename ===
			'AgentStudioHasVersionCapability' &&
		Boolean(versionCapabilityData.agentStudio_hasVersionCapability.hasVersionCapability);

	const permissionsData = useLazyLoadQuery<AgentVersionResolverPermissionsQuery>(
		graphql`
			query AgentVersionResolverPermissionsQuery($agentAri: ID!, $skip: Boolean!) {
				agentStudio_agentById(id: $agentAri) @optIn(to: "AgentStudio") @skip(if: $skip) {
					__typename
					... on AgentStudioAssistant {
						permissions {
							canManage
						}
					}
				}
			}
		`,
		{
			agentAri: agentAri ?? '',
			skip: !agentAri || !hasVersionCapability,
		},
	);
	const canManage =
		permissionsData?.agentStudio_agentById?.__typename === 'AgentStudioAssistant' &&
		permissionsData.agentStudio_agentById.permissions?.canManage === true;

	useEffect(() => {
		if (agentVersion || !currentConversationId) {
			return;
		}

		setConversationData({
			conversationId: currentConversationId,
			data: {
				agentVersion: {
					versionType: hasVersionCapability && canManage ? 'DRAFT' : 'LIVE',
				},
			},
		});
	}, [agentVersion, canManage, currentConversationId, hasVersionCapability, setConversationData]);

	return agentVersion || !currentConversationId ? (
		<>
			{children({
				agentAri: agentAri ?? null,
				canManage,
				hasVersionCapability,
			})}
		</>
	) : (
		<>{fallback}</>
	);
};
